/* global Intl */
import { escapeHtml } from '../dom/escape-html.js';
import { formatDate, formatDateTime } from '../locale/format-date.js';

/**
 * Renders a value as a badge with a color class determined by map lookup.
 * Returns a safe HTML string.
 *
 * @param {unknown} value
 * @param {Record<string, string>} map - Maps value strings to badge class names.
 * @returns {string}
 */
export function renderBadge(value, map) {
  const str = value == null ? '' : String(value);
  const cls = map[str] ?? '';
  return `<span class="badge ${escapeHtml(cls)}">${escapeHtml(str)}</span>`;
}

// Accepted string shapes (matched before slicing to yyyy-MM-dd):
//   ISO_DATE_ONLY — exact "yyyy-MM-dd", no trailing garbage.
//   ISO_DATETIME  — "yyyy-MM-dd" + T/space separator + range-valid HH:MM[:SS[.fff]]
//                   + optional Z or ±HH:MM / ±HHMM offset. Anchored: no trailing junk.
// Range-aware fields (00–23 hours, 00–59 minutes/seconds) reject shape-valid but
// semantically-invalid inputs like "2024-06-15T24:00" so malformed DB values stay
// visible rather than being silently normalized into a formatted date.
const ISO_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d+)?)?(Z|[+-]([01]\d|2[0-3]):?[0-5]\d)?$/;

/**
 * Renders a date/datetime value for a grid cell, honoring the active locale
 * config so the column matches the filter trigger's formatted display.
 *
 * @param {unknown} value - ISO date-only string ("yyyy-MM-dd"), ISO datetime
 *   string with "T" or " " separator (e.g. "2024-06-15T10:30:00Z",
 *   "2024-06-15 10:30:00"), a Date instance, a finite numeric timestamp,
 *   or other.
 * @param {import('../../contracts/locale.js').LocaleConfig} [localeConfig]
 *   Active locale config. When `localeConfig.dateFormat` is present, format
 *   via the app's locale formatter. When absent: `Date`/numeric inputs fall
 *   back to `Intl.DateTimeFormat()`; string inputs are returned raw-escaped
 *   (no implicit `new Date(string)` parsing — avoids the UTC-midnight trap
 *   and silent calendar-rollover on invalid ISO dates).
 * @returns {string} HTML markup intended for use with a `renderHtml: true`
 *   column. Dynamic content is escaped; output is always wrapped in a
 *   `<span class="nowrap">` so dates don't break across lines.
 */
export function renderDate(value, localeConfig) {
  if (value == null || value === '') return '';

  const raw = `<span class="nowrap">${escapeHtml(String(value))}</span>`;

  // Compute normalized: a candidate ISO date string, a Date, or null.
  /** @type {string | Date | null} */
  let normalized = null;
  if (typeof value === 'string') {
    if (ISO_DATE_ONLY_RE.test(value) || ISO_DATETIME_RE.test(value)) {
      normalized = value.slice(0, 10);
    }
  } else if (value instanceof Date) {
    if (!Number.isNaN(value.getTime())) normalized = value;
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) normalized = d;
  }

  // Case A — un-normalizable input stays visible, raw-escaped.
  if (normalized === null) return raw;

  const dateFormat = localeConfig && localeConfig.dateFormat;

  // Case B — candidate ISO date string.
  if (typeof normalized === 'string') {
    if (!dateFormat) return raw;
    const formatted = formatDate(normalized, dateFormat);
    // formatDate returns '' for calendar-invalid dates (e.g. "2024-02-31").
    // Do NOT fall through to new Date(normalized) + Intl — that would silently
    // roll "2024-02-31" to March 2 and render wrong data as a clean date.
    if (formatted === '') return raw;
    return `<span class="nowrap">${escapeHtml(formatted)}</span>`;
  }

  // Case C — Date instance (from Date or numeric timestamp input).
  if (dateFormat) {
    const formatted = formatDate(normalized, dateFormat);
    if (formatted !== '') return `<span class="nowrap">${escapeHtml(formatted)}</span>`;
  }
  try {
    return `<span class="nowrap">${escapeHtml(new Intl.DateTimeFormat().format(normalized))}</span>`;
  } catch {
    return raw;
  }
}

// Datetime wire shapes accepted (the same 5 shapes formatDateTime accepts):
//   yyyy-MM-dd, yyyy-MM-dd HH:mm, yyyy-MM-dd HH:mm:ss,
//   yyyy-MM-ddTHH:mm, yyyy-MM-ddTHH:mm:ss
const DATETIME_WIRE_RE_RENDER =
  /^\d{4}-\d{2}-\d{2}(?:[T ]([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?)?$/;

/**
 * Renders a datetime value for a grid cell, honoring the active locale config.
 * The renderer composes `formatDate(d, dateFormat) + ' ' + formatTime(t, timeFormat)`
 * — `localeConfig.datetimeFormat` is stored-only metadata in the current PR
 * and is NOT consumed here.
 *
 * Accepts:
 *   - The 5 datetime wire shapes (date-only normalizes to `'YYYY-MM-DD 00:00'`
 *     under PL locale's `'HH:mm'` time format).
 *   - A `Date` instance.
 *   - A finite numeric timestamp (converted via `new Date(num)`).
 *
 * @param {unknown} value
 * @param {import('../../contracts/locale.js').LocaleConfig} [localeConfig]
 * @returns {string} HTML markup intended for use with a `renderHtml: true`
 *   column. Dynamic content is escaped; output is always wrapped in
 *   `<span class="nowrap">`.
 */
export function renderDateTime(value, localeConfig) {
  if (value == null || value === '') return '';

  const raw = `<span class="nowrap">${escapeHtml(String(value))}</span>`;

  // Compute normalized: a candidate datetime wire string, a Date, or null.
  /** @type {string | Date | null} */
  let normalized = null;
  if (typeof value === 'string') {
    if (DATETIME_WIRE_RE_RENDER.test(value)) {
      normalized = value;
    }
  } else if (value instanceof Date) {
    if (!Number.isNaN(value.getTime())) normalized = value;
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) normalized = d;
  }

  if (normalized === null) return raw;

  const dateFormat = localeConfig && localeConfig.dateFormat;
  const timeFormat = localeConfig && localeConfig.timeFormat;

  // String / Date — pass to formatDateTime; if it can't normalize / format, raw fallback.
  if (dateFormat && timeFormat) {
    const formatted = formatDateTime(normalized, dateFormat, timeFormat, ' ');
    if (formatted !== '') {
      return `<span class="nowrap">${escapeHtml(formatted)}</span>`;
    }
  }
  // Locale missing or formatDateTime rejected — raw fallback.
  // For Date instances without a localeConfig we attempt Intl as a last resort,
  // mirroring renderDate's Case C.
  if (normalized instanceof Date) {
    try {
      return `<span class="nowrap">${escapeHtml(new Intl.DateTimeFormat().format(normalized))}</span>`;
    } catch {
      return raw;
    }
  }
  return raw;
}

/**
 * Renders a truthy/falsy value as a Yes/No badge.
 * Returns a safe HTML string.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function renderBoolean(value) {
  const isYes = value === 1 || value === '1' || value === true || value === 'true';
  if (isYes) {
    return '<span class="badge badge--green">Yes</span>';
  }
  return '<span class="badge badge--gray">No</span>';
}

/**
 * Renders a string truncated to max characters, with a title attribute showing the full value.
 * Returns a safe HTML string.
 *
 * @param {unknown} value
 * @param {number} max - Maximum characters before truncation.
 * @returns {string}
 */
export function renderTruncated(value, max) {
  const str = value == null ? '' : String(value);
  if (str.length <= max) {
    return escapeHtml(str);
  }
  const truncated = str.slice(0, max) + '…';
  return `<span title="${escapeHtml(str)}">${escapeHtml(truncated)}</span>`;
}
